import { useState, useEffect, useMemo } from 'react';

interface UseScoreboardWidthDynamicProps {
    homeTeam: string | number;
    awayTeam: string | number;
    staticPadding?: number; 
    clockWidth?: number; 
    containerPadding?: number; 
}

interface ScoreboardWidthResult {
    teamWidth: number;
    homeWidth: number;
    awayWidth: number;
    maxScoreboardWidth: number;
    screenWidth: number;
    separatorWidth: number;
    homeTeamWidth: number;
    awayTeamWidth: number;
    actualScoreboardWidth: number;
}

export const useScoreboardWidthDynamicFormation = ({ 
    homeTeam, 
    awayTeam, 
    staticPadding = 32, 
    clockWidth = 0,
    containerPadding = 24
}: UseScoreboardWidthDynamicProps): ScoreboardWidthResult => {
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    // Listen for screen width changes
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const SEPARATOR_WIDTH = 32; 
    const SM_PADDING = 12; 
    const XS_PADDING = 8; 
    const SHIELD_WIDTH = 24; 
    const LETTER_WIDTH = 7.5; 
    const MIN_TEAM_WIDTH = 40; 
    const GAP_WIDTH = 4; 

    const calculateTeamNameWidth = (teamName: string | number) => {
        const teamStr = String(teamName);
        return Math.max(teamStr.length * LETTER_WIDTH, MIN_TEAM_WIDTH);
    };

    // Calculate max scoreboard width - full width minus 8px from each side
    const maxScoreboardWidth = useMemo(() => {
        return screenWidth - (XS_PADDING * 2);
    }, [screenWidth]);

    // Calculate available width for team names - considering shield padding and gap
    const calculateAvailableWidth = () => {
        return maxScoreboardWidth - (SM_PADDING * 2) - SEPARATOR_WIDTH - (SHIELD_WIDTH * 2) - (GAP_WIDTH * 2);
    };

    // Main logic for calculating widths
    const calculateWidths = useMemo(() => {
        const homeTeamStr = String(homeTeam);
        const awayTeamStr = String(awayTeam);
        
        const homeNameWidth = calculateTeamNameWidth(homeTeam);
        const awayNameWidth = calculateTeamNameWidth(awayTeam);
        const availableWidth = calculateAvailableWidth();

        // If both names fit in available space
        if (homeNameWidth + awayNameWidth <= availableWidth) {
            return {
                homeTeamWidth: homeNameWidth,
                awayTeamWidth: awayNameWidth,
                separatorWidth: SEPARATOR_WIDTH
            };
        }

        // If one name is much shorter, give it full width
        const shorterTeam = homeNameWidth <= awayNameWidth ? 'home' : 'away';
        const shorterWidth = shorterTeam === 'home' ? homeNameWidth : awayNameWidth;
        const longerWidth = shorterTeam === 'home' ? awayNameWidth : homeNameWidth;

        // Check if shorter name fits in half of available space
        const halfAvailableWidth = availableWidth / 2;
        
        if (shorterWidth <= halfAvailableWidth) {
            // Shorter name fits, give it full width
            const remainingWidth = availableWidth - shorterWidth;
            
            if (shorterTeam === 'home') {
                return {
                    homeTeamWidth: shorterWidth,
                    awayTeamWidth: remainingWidth,
                    separatorWidth: SEPARATOR_WIDTH
                };
            } else {
                return {
                    homeTeamWidth: remainingWidth,
                    awayTeamWidth: shorterWidth,
                    separatorWidth: SEPARATOR_WIDTH
                };
            }
        } else {
            // Both names must be truncated - center separator
            const equalWidth = availableWidth / 2;
            return {
                homeTeamWidth: equalWidth,
                awayTeamWidth: equalWidth,
                separatorWidth: SEPARATOR_WIDTH
            };
        }
    }, [homeTeam, awayTeam, maxScoreboardWidth]);

    // Calculate actual scoreboard width - exactly like in component
    const actualScoreboardWidth = useMemo(() => {
        // In component: width: `${homeTeamWidth + 24 + 24}px` (name + shield + gap + padding)
        const homeSectionWidth = calculateWidths.homeTeamWidth + SHIELD_WIDTH + GAP_WIDTH + (SM_PADDING * 2);
        const awaySectionWidth = calculateWidths.awayTeamWidth + SHIELD_WIDTH + GAP_WIDTH + (SM_PADDING * 2);
        
        // Add separator and main container padding
        const totalWidth = homeSectionWidth + calculateWidths.separatorWidth + awaySectionWidth + (XS_PADDING * 2);
        
        // Check if names fit in available space
        const homeNameWidth = calculateTeamNameWidth(homeTeam);
        const awayNameWidth = calculateTeamNameWidth(awayTeam);
        const availableWidth = calculateAvailableWidth();
        
        // If both names fit in available space, reduce width by 40
        if (homeNameWidth + awayNameWidth <= availableWidth) {
            return Math.min(totalWidth - 40, maxScoreboardWidth);
        }
        
        return Math.min(totalWidth, maxScoreboardWidth);
    }, [calculateWidths, maxScoreboardWidth, homeTeam, awayTeam]);

    return {
        teamWidth: Math.max(calculateWidths.homeTeamWidth, calculateWidths.awayTeamWidth),
        homeWidth: calculateWidths.homeTeamWidth,
        awayWidth: calculateWidths.awayTeamWidth,
        maxScoreboardWidth,
        screenWidth,
        separatorWidth: calculateWidths.separatorWidth,
        homeTeamWidth: calculateWidths.homeTeamWidth,
        awayTeamWidth: calculateWidths.awayTeamWidth,
        actualScoreboardWidth
    };
}; 